import { useTranslation } from "react-i18next"

import type {
  SkillPackageBudgets,
  SkillPackageFormState,
  SkillPackageFormValidation,
} from "../utils/skill-package-draft"

import {
  MAX_SKILL_PACKAGE_DESCRIPTION_LENGTH,
  MAX_SKILL_PACKAGE_DISPLAY_NAME_LENGTH,
  MAX_SKILL_PACKAGE_NAME_LENGTH,
} from "@/api/admin/skill-packages"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"

import { SkillPackageAiAssistant } from "./skill-package-ai-assistant"
import { SkillPackageBudgetCounter, SkillPackageFormError } from "./skill-package-editor-feedback"

// The "Skill" tab (#3074): the assistant, the naming, and the four pieces of
// text that ARE the skill.
//
// The slug is the one field that locks after creation, because it is what an
// installed agent resolves `load_skill` by — renaming it later would silently
// break every agent that pinned the package. The field says so rather than
// just going grey.

export function SkillPackageContentTab({
  form,
  patch,
  budgets,
  validation,
  showErrors,
  busy,
  isSaved,
  onGeneratedDraft,
  onDisplayNameChange,
  onNameChange,
}: {
  form: SkillPackageFormState
  patch: (partial: Partial<SkillPackageFormState>) => void
  budgets: SkillPackageBudgets
  validation: SkillPackageFormValidation
  showErrors: boolean
  busy: boolean
  isSaved: boolean
  onGeneratedDraft: (next: SkillPackageFormState) => void
  onDisplayNameChange: (value: string) => void
  onNameChange: (value: string) => void
}) {
  const { t } = useTranslation()

  return (
    <div className="space-y-4">
      <SkillPackageAiAssistant disabled={busy} form={form} onDraft={onGeneratedDraft} />

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="skill-package-display-name">
            {t("adminDashboard.skillPackages.editor.displayName", { defaultValue: "Name" })}
          </Label>
          <Input
            data-testid="skill-package-display-name"
            id="skill-package-display-name"
            maxLength={MAX_SKILL_PACKAGE_DISPLAY_NAME_LENGTH}
            value={form.displayName}
            onChange={(event) => onDisplayNameChange(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="skill-package-name">
            {t("adminDashboard.skillPackages.editor.name", { defaultValue: "Slug" })}
          </Label>
          <Input
            className="font-mono"
            data-testid="skill-package-name"
            disabled={isSaved}
            id="skill-package-name"
            maxLength={MAX_SKILL_PACKAGE_NAME_LENGTH}
            value={form.name}
            onChange={(event) => onNameChange(event.target.value)}
          />
          <p className="text-muted-foreground text-xs">
            {isSaved
              ? t("adminDashboard.skillPackages.editor.nameLocked", {
                  defaultValue:
                    "The slug is what installed agents resolve the skill by; it cannot change after the package is created.",
                })
              : t("adminDashboard.skillPackages.editor.nameHelp", {
                  defaultValue:
                    "Lowercase letters, digits and hyphens. This is the name the agent loads with load_skill.",
                })}
          </p>
          <SkillPackageFormError
            codes={validation.form}
            match={["name_required", "name_not_a_slug", "name_too_long"]}
            show={showErrors}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="skill-package-description">
          {t("adminDashboard.skillPackages.editor.descriptionLabel", {
            defaultValue: "Description",
          })}
        </Label>
        <Textarea
          data-testid="skill-package-description"
          id="skill-package-description"
          maxLength={MAX_SKILL_PACKAGE_DESCRIPTION_LENGTH}
          rows={2}
          value={form.description}
          onChange={(event) => patch({ description: event.target.value })}
        />
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.skillPackages.editor.descriptionHelp", {
            defaultValue:
              "What the skill does and when to reach for it. The agent reads this before deciding to load the skill.",
          })}
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label htmlFor="skill-package-instructions">
            {t("adminDashboard.skillPackages.editor.instructions", {
              defaultValue: "Instructions (markdown)",
            })}
          </Label>
          <SkillPackageBudgetCounter budget={budgets.instructions} unit="bytes" />
        </div>
        <Textarea
          className="font-mono text-xs"
          data-testid="skill-package-instructions"
          id="skill-package-instructions"
          rows={12}
          value={form.instructions}
          onChange={(event) => patch({ instructions: event.target.value })}
        />
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.skillPackages.editor.instructionsHelp", {
            defaultValue:
              "The body of SKILL.md — the method the agent follows once it loads the skill. Markdown headings for when the skill applies and the working steps.",
          })}
        </p>
        <SkillPackageFormError
          codes={validation.form}
          match={["instructions_required", "instructions_too_long"]}
          show={showErrors}
        />
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label htmlFor="skill-package-delivery">
            {t("adminDashboard.skillPackages.editor.deliveryContract", {
              defaultValue: "Delivery contract",
            })}
          </Label>
          <SkillPackageBudgetCounter budget={budgets.deliveryContract} unit="bytes" />
        </div>
        <Textarea
          data-testid="skill-package-delivery"
          id="skill-package-delivery"
          rows={3}
          value={form.deliveryContract}
          onChange={(event) => patch({ deliveryContract: event.target.value })}
        />
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.skillPackages.editor.deliveryContractHelp", {
            defaultValue:
              "What a finished answer must contain, and in what shape. It is appended to the loaded skill as its own section.",
          })}
        </p>
        <SkillPackageFormError
          codes={validation.form}
          match={["delivery_contract_too_long"]}
          show={showErrors}
        />
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label htmlFor="skill-package-excerpt">
            {t("adminDashboard.skillPackages.editor.guidanceExcerpt", {
              defaultValue: "System-prompt excerpt",
            })}
          </Label>
          <SkillPackageBudgetCounter budget={budgets.guidanceExcerpt} unit="bytes" />
        </div>
        <Textarea
          data-testid="skill-package-excerpt"
          id="skill-package-excerpt"
          rows={2}
          value={form.guidanceExcerpt}
          onChange={(event) => patch({ guidanceExcerpt: event.target.value })}
        />
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.skillPackages.editor.guidanceExcerptHelp", {
            defaultValue:
              "One or two lines listed in the agent's system prompt so it knows the skill exists. Keep it short — prompt space is the scarcest budget.",
          })}
        </p>
        <SkillPackageFormError
          codes={validation.form}
          match={["guidance_excerpt_too_long"]}
          show={showErrors}
        />
      </div>
    </div>
  )
}
